// src/components/Admin/Cantieri/LavorazioniManagement.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../../firebase';
import './MasterDataManagement.css';
import './LavorazioniManagement.css';

const LavorazioniManagement = () => {
  const [lavorazioni, setLavorazioni] = useState([]);
  const [filteredLavorazioni, setFilteredLavorazioni] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [formData, setFormData] = useState({
    nome: ''
  });

  // Lista iniziale delle lavorazioni
  const lavorazioniIniziali = [
    'Posa Armatura',
    'Bocciardatura',
    'Esecuzione di carotaggi',
    'Realizzazione Carpenteria',
    'Demolizione',
    'Realizzazione fori ed inghisaggi',
    'Fornitura cls',
    'Fresatura',
    'Getto cls',
    'Idrodemolizione',
    'Idrosabbiatura',
    'Impermeabilizzazione',
    'Inghisaggi',
    'Movimentazione terre e rocce da scavo',
    'Passivazione',
    'Perforazioni',
    'Pitturazione',
    'Posa barriere',
    'Posa ferri d\'armatura',
    'Posa linea vita',
    'Posa malta',
    'Posa ponteggio',
    'Posa rasante',
    'Pulizia e sistemazione',
    'Esecuzione di rilievi topografici',
    'Scavo',
    'Smaltimento rifiuti',
    'Stesa conglomerato bituminoso',
    'Tinteggiatura',
    'Varie'
  ];

  // Caricamento lavorazioni da Firebase
  const loadLavorazioni = async () => {
    try {
      setIsLoading(true);
      const lavorazioniQuery = query(
        collection(db, 'lavorazioni'),
        orderBy('nome', 'asc')
      );
      
      const snapshot = await getDocs(lavorazioniQuery);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setLavorazioni(data);
      console.log(`Caricate ${data.length} lavorazioni`);
    } catch (error) {
      console.error("Errore nel caricamento lavorazioni:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Importazione massiva delle lavorazioni iniziali
  const importLavorazioniIniziali = async () => {
    try {
      setIsLoading(true);
      console.log('Avvio importazione lavorazioni iniziali...');
      
      const batch = [];
      for (const nomeLavorazione of lavorazioniIniziali) {
        const lavorazioneData = {
          nome: nomeLavorazione,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        batch.push(addDoc(collection(db, 'lavorazioni'), lavorazioneData));
      }
      
      await Promise.all(batch);
      console.log(`Importate ${lavorazioniIniziali.length} lavorazioni iniziali`);
      
      // Ricarica i dati
      await loadLavorazioni();
      setShowBulkImport(false);
    } catch (error) {
      console.error("Errore nell'importazione lavorazioni:", error);
      alert('Errore durante l\'importazione delle lavorazioni');
    } finally {
      setIsLoading(false);
    }
  };

  // Applicazione filtri di ricerca
  const applyFilters = () => {
    let filtered = [...lavorazioni];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(lavorazione => 
        lavorazione.nome.toLowerCase().includes(search)
      );
    }

    setFilteredLavorazioni(filtered);
  };

  // Reset del form
  const resetForm = () => {
    setFormData({
      nome: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Gestione input form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Salvataggio lavorazione
  const saveLavorazione = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      alert('Il nome della lavorazione è obbligatorio');
      return;
    }

    try {
      setIsLoading(true);
      
      const lavorazioneData = {
        nome: formData.nome.trim(),
        updatedAt: new Date()
      };

      if (editingId) {
        // Aggiornamento
        const lavorazioneRef = doc(db, 'lavorazioni', editingId);
        await updateDoc(lavorazioneRef, lavorazioneData);
        console.log('Lavorazione aggiornata:', editingId);
      } else {
        // Creazione
        lavorazioneData.createdAt = new Date();
        await addDoc(collection(db, 'lavorazioni'), lavorazioneData);
        console.log('Nuova lavorazione creata');
      }

      resetForm();
      await loadLavorazioni();
    } catch (error) {
      console.error("Errore nel salvataggio lavorazione:", error);
      alert('Errore nel salvataggio della lavorazione');
    } finally {
      setIsLoading(false);
    }
  };

  // Modifica lavorazione
  const editLavorazione = (lavorazione) => {
    setFormData({
      nome: lavorazione.nome
    });
    setEditingId(lavorazione.id);
    setShowForm(true);
  };

  // Eliminazione lavorazione
  const deleteLavorazione = async (lavorazioneId, nomeLavorazione) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la lavorazione "${nomeLavorazione}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      const lavorazioneRef = doc(db, 'lavorazioni', lavorazioneId);
      await deleteDoc(lavorazioneRef);
      console.log('Lavorazione eliminata:', lavorazioneId);
      await loadLavorazioni();
    } catch (error) {
      console.error("Errore nell'eliminazione lavorazione:", error);
      alert('Errore nell\'eliminazione della lavorazione');
    } finally {
      setIsLoading(false);
    }
  };

  // Caricamento iniziale
  useEffect(() => {
    loadLavorazioni();
  }, []);

  // Applicazione filtri quando cambiano i dati o i filtri
  useEffect(() => {
    applyFilters();
  }, [lavorazioni, searchTerm]);

  if (isLoading) {
    return (
      <div className="master-data-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Caricamento lavorazioni in corso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-data-container">
      {/* Header */}
      <div className="master-data-header">
        <div className="header-info">
          <h2>⚒️ Gestione Lavorazioni</h2>
          <p>Gestisci le tipologie di lavorazioni disponibili per i rapportini</p>
        </div>
        <div className="header-actions">
          {lavorazioni.length === 0 && (
            <button 
              className="btn btn-info"
              onClick={() => setShowBulkImport(true)}
            >
              📥 Importa Lavorazioni Standard
            </button>
          )}
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            ➕ Nuova Lavorazione
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Cerca lavorazione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        <div className="results-count">
          {filteredLavorazioni.length} lavorazioni trovate su {lavorazioni.length} totali
        </div>
      </div>

      {/* Lista lavorazioni */}
      <div className="master-data-list">
        {filteredLavorazioni.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">⚒️</div>
            <h3>Nessuna lavorazione trovata</h3>
            <p>
              {lavorazioni.length === 0 
                ? "Non ci sono lavorazioni nel sistema. Inizia importando le lavorazioni standard o aggiungendo la prima lavorazione."
                : "Nessuna lavorazione corrisponde alla ricerca."
              }
            </p>
          </div>
        ) : (
          <div className="data-grid">
            {filteredLavorazioni.map(lavorazione => (
              <div key={lavorazione.id} className="data-card lavorazione-card">
                <div className="card-header">
                  <h3 className="card-title">{lavorazione.nome}</h3>
                  <div className="card-actions">
                    <button 
                      className="btn-icon btn-edit"
                      onClick={() => editLavorazione(lavorazione)}
                      title="Modifica lavorazione"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button 
                      className="btn-icon btn-delete"
                      onClick={() => deleteLavorazione(lavorazione.id, lavorazione.nome)}
                      title="Elimina lavorazione"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="card-content">
                  <div className="info-row">
                    <span className="info-label">📝 Tipo:</span>
                    <span className="info-value">Lavorazione cantiere</span>
                  </div>
                  
                  {lavorazione.createdAt && (
                    <div className="info-row">
                      <span className="info-label">📅 Creata:</span>
                      <span className="info-value">
                        {new Date(lavorazione.createdAt.seconds * 1000).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content lavorazione-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifica Lavorazione' : 'Nuova Lavorazione'}</h3>
              <button className="modal-close" onClick={resetForm}>✕</button>
            </div>
            
            <form onSubmit={saveLavorazione} className="modal-form">
              <div className="form-group required">
                <label>Nome Lavorazione *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  placeholder="es. Getto cls, Posa armatura, Demolizione..."
                  required
                  autoFocus
                />
                <small className="form-help">
                  Inserisci il nome della lavorazione che apparirà nei rapportini
                </small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Aggiorna Lavorazione' : 'Crea Lavorazione'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Importazione Massiva */}
      {showBulkImport && (
        <div className="modal-overlay" onClick={() => setShowBulkImport(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📥 Importa Lavorazioni Standard</h3>
              <button className="modal-close" onClick={() => setShowBulkImport(false)}>✕</button>
            </div>
            
            <div className="modal-body">
              <p>Stai per importare <strong>{lavorazioniIniziali.length} lavorazioni</strong> standard nel sistema.</p>
              <p>Queste sono le lavorazioni più comuni utilizzate nei cantieri edili.</p>
              
              <div className="preview-list">
                <h4>Anteprima lavorazioni da importare:</h4>
                <div className="preview-items">
                  {lavorazioniIniziali.slice(0, 10).map((lavorazione, index) => (
                    <div key={index} className="preview-item">
                      <strong>{lavorazione}</strong>
                    </div>
                  ))}
                  {lavorazioniIniziali.length > 10 && (
                    <div className="preview-more">
                      ... e altre {lavorazioniIniziali.length - 10} lavorazioni
                    </div>
                  )}
                </div>
                
                <div className="import-summary">
                  <h4>Esempi di lavorazioni incluse:</h4>
                  <ul>
                    <li><strong>Strutturali:</strong> Posa Armatura, Getto cls, Carpenteria</li>
                    <li><strong>Demolizioni:</strong> Demolizione, Idrodemolizione, Fresatura</li>
                    <li><strong>Finiture:</strong> Impermeabilizzazione, Pitturazione, Tinteggiatura</li>
                    <li><strong>Movimento terra:</strong> Scavo, Movimentazione terre e rocce</li>
                    <li><strong>Specialistiche:</strong> Carotaggi, Perforazioni, Rilievi topografici</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowBulkImport(false)}
              >
                Annulla
              </button>
              <button 
                type="button" 
                className="btn btn-success" 
                onClick={importLavorazioniIniziali}
              >
                Conferma Importazione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LavorazioniManagement;